import { DateTime } from 'luxon';
import { SideMenuDropdown } from '@barracuda-internal/bds-core-v2';

export const detectionSearchFields = [
	'file',
	'owner',
	'filePath',
	'fileExt',
	'creator',
];

export type ColumnKey =
	| 'fileName'
	| 'lastModified'
	| 'cloudType'
	| 'filePath'
	| 'ownerNames'
	| 'violations'
	| 'sharingLevels'
	| 'tags'
	| 'size'
	| 'fileExtension'
	| 'significantFindingCategories'
	| 'statusReason'
	| 'resolvedBy';

export const COLUMN_DEFINITIONS: { accessorKey: ColumnKey; header: string }[] =
	[
		{ accessorKey: 'statusReason', header: 'Action' },
		{ accessorKey: 'fileName', header: 'File' },
		{ accessorKey: 'cloudType', header: 'Platform' },
		{ accessorKey: 'lastModified', header: 'Last Detected' },
		{ accessorKey: 'ownerNames', header: 'Owner / Creator' },
		{ accessorKey: 'violations', header: 'Violations' },
		{ accessorKey: 'sharingLevels', header: 'Sharing' },
		{ accessorKey: 'tags', header: 'Classifiers' },
	];

// Tab-based column definitions
// Tab 0: Unresolved - no Action column
// Tab 1: In Progress - Action column first
// Tab 2: Resolved - Action first, "Last Detected" becomes "Resolved", adds "Resolved By"
export const TAB_COLUMNS: Record<
	number,
	{ accessorKey: ColumnKey; header: string }[]
> = {
	0: [
		{ accessorKey: 'fileName', header: 'File' },
		{ accessorKey: 'cloudType', header: 'Platform' },
		{ accessorKey: 'lastModified', header: 'Last Detected' },
		{ accessorKey: 'ownerNames', header: 'Owner / Creator' },
		{ accessorKey: 'violations', header: 'Violations' },
		{ accessorKey: 'sharingLevels', header: 'Sharing' },
		{ accessorKey: 'tags', header: 'Classifiers' },
	],
	1: [
		{ accessorKey: 'statusReason', header: 'Action' },
		{ accessorKey: 'fileName', header: 'File' },
		{ accessorKey: 'cloudType', header: 'Platform' },
		{ accessorKey: 'lastModified', header: 'Last Detected' },
		{ accessorKey: 'ownerNames', header: 'Owner / Creator' },
		{ accessorKey: 'violations', header: 'Violations' },
		{ accessorKey: 'sharingLevels', header: 'Sharing' },
		{ accessorKey: 'tags', header: 'Classifiers' },
	],
	2: [
		{ accessorKey: 'statusReason', header: 'Action' },
		{ accessorKey: 'fileName', header: 'File' },
		{ accessorKey: 'cloudType', header: 'Platform' },
		{ accessorKey: 'lastModified', header: 'Resolved' },
		{ accessorKey: 'resolvedBy', header: 'Resolved By' },
		{ accessorKey: 'ownerNames', header: 'Owner / Creator' },
		{ accessorKey: 'violations', header: 'Violations' },
		{ accessorKey: 'sharingLevels', header: 'Sharing' },
		{ accessorKey: 'tags', header: 'Classifiers' },
	],
};

export const COLUMNS_WITHOUT_FILTERING: ColumnKey[] = [
	'fileName',
	'lastModified',
	'ownerNames',
	'violations',
	'statusReason',
	'resolvedBy',
	'filePath',
	'size',
	'fileExtension',
	'significantFindingCategories',
];

export const DETECTION_FILTER_POSSIBLE_VALUES: Record<
	string,
	{ possibleValues: Record<string, string> | null }
> = {
	cloudType: {
		possibleValues: {
			OneDriveConnector: 'OneDriveConnector',
			SharepointLibraryConnector: 'SharepointLibraryConnector',
		},
	},
	sharingLevels: {
		possibleValues: {
			Public: 'Public',
			Internal: 'Internal',
			Private: 'Private',
			External: 'External',
			Protected: 'Protected',
			Tenant: 'Tenant',
		},
	},
	tags: { possibleValues: null },
};

export const DETECTION_FILTER_CONDITIONS = [
	{ label: 'Contains', value: 'Contains' },
	{ label: 'Is equal to', value: 'Equals' },
];

// Display labels for filter field names
export const FILTER_FIELD_LABELS: Record<string, string> = {
	cloudType: 'Platform',
	sharingLevels: 'Sharing',
	tags: 'Classifiers',
};

// Options for the search field dropdown in the filter popover
export const SEARCH_FIELD_OPTIONS = [
	{ label: 'File Name', value: 'file' },
	{ label: 'Owner', value: 'owner' },
	{ label: 'File Path', value: 'filePath' },
	{ label: 'File Extension', value: 'fileExt' },
	{ label: 'Creator', value: 'creator' },
];

// Options for the filter type dropdown in the filter popover
export const FILTER_TYPE_OPTIONS = [
	{ label: 'Platform', value: 'cloudType' },
	{ label: 'Sharing', value: 'sharingLevels' },
	{ label: 'Classifiers', value: 'tags' },
];

// Row type options for the unified filter popover (V2)
export const ROW_TYPE_OPTIONS = [
	{ label: 'Search', value: 'search' },
	{ label: 'Filter', value: 'filter' },
];

export const COLUMNS_WITHOUT_SORTING: ColumnKey[] = [
	'statusReason',
	'cloudType',
	'ownerNames',
	'sharingLevels',
	'tags',
	'filePath',
	'significantFindingCategories',
	'resolvedBy',
];

// Maps table accessorKey to the backend sort field name
export const SORT_FIELD_MAP: Record<string, string> = {
	fileName: 'file',
	lastModified: 'lastUpdated',
	violations: 'violations',
};

// Reverse map: backend sort field -> table accessorKey
export const SORT_FIELD_REVERSE_MAP: Record<string, string> = {
	file: 'fileName',
	lastUpdated: 'lastModified',
	violations: 'violations',
};

export const DATE_SHORTCUTS = [
	{
		label: 'Last day',
		getValue: (): [DateTime, DateTime] => {
			const now = DateTime.local();
			return [now.minus({ days: 1 }), now];
		},
	},
	{
		label: 'Last 3 days',
		getValue: (): [DateTime, DateTime] => {
			const now = DateTime.local();
			return [now.minus({ days: 3 }), now];
		},
	},
	{
		label: 'Last 7 days',
		getValue: (): [DateTime, DateTime] => {
			const now = DateTime.local();
			return [now.minus({ days: 7 }), now];
		},
	},
	{
		label: 'Last 30 days',
		getValue: (): [DateTime, DateTime] => {
			const now = DateTime.local();
			return [now.minus({ days: 30 }), now];
		},
	},
	{
		label: 'Last 90 days',
		getValue: (): [DateTime, DateTime] => {
			const now = DateTime.local();
			return [now.minus({ days: 90 }), now];
		},
	},
];

// Side menu dropdown items for column visibility (tabs 0 & 1)
export const SIDE_MENU_DROPDOWNS_TAB_01: SideMenuDropdown[] = [
	{
		label: 'Columns',
		items: [
			{ id: 'cloudType', label: 'Platform' },
			{ id: 'lastModified', label: 'Last Detected' },
			{ id: 'ownerNames', label: 'Owner / Creator' },
			{ id: 'violations', label: 'Violations' },
			{ id: 'sharingLevels', label: 'Sharing' },
			{ id: 'tags', label: 'Classifiers' },
		],
	},
];

// Side menu dropdown items for column visibility (tab 2)
export const SIDE_MENU_DROPDOWNS_TAB_2: SideMenuDropdown[] = [
	{
		label: 'Columns',
		items: [
			{ id: 'cloudType', label: 'Platform' },
			{ id: 'lastModified', label: 'Resolved' },
			{ id: 'resolvedBy', label: 'Resolved By' },
			{ id: 'ownerNames', label: 'Owner / Creator' },
			{ id: 'violations', label: 'Violations' },
			{ id: 'sharingLevels', label: 'Sharing' },
			{ id: 'tags', label: 'Classifiers' },
		],
	},
];

// Initial column visibility state for tabs 0 & 1
export const COLUMN_VISIBILITY_INITIAL_STATE_TAB_01: Record<string, boolean> = {
	cloudType: true,
	lastModified: true,
	ownerNames: true,
	violations: true,
	sharingLevels: true,
	tags: true,
};

// Initial column visibility state for tab 2
export const COLUMN_VISIBILITY_INITIAL_STATE_TAB_2: Record<string, boolean> = {
	cloudType: true,
	lastModified: true,
	resolvedBy: true,
	ownerNames: true,
	violations: true,
	sharingLevels: true,
	tags: true,
};
